import { memo, useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { type Node, type NodeProps, Position } from '@xyflow/react'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import { useDeleteNode } from '@/hooks/use-delete-node'
import CustomHandle from '@/features/flow/_builder/components/handles/custom-handler'
import {
  NodeCard,
  NodeCardContent,
  NodeCardDescription,
  NodeCardFooter,
  NodeCardHeader,
} from '@/features/flow/_builder/components/ui/node-card'
import FlowConnectionPropertyPanel from '../../sidebar/panels/node-properties/property-panels/flow-connection-property-panel'
import { BuilderNode, RegisterNodeMetadata } from '../../types'
import { getNodeDetail } from '../../utils'

const NODE_TYPE = BuilderNode.FLOW_CONNECTION

const FlowConnectionNodeDataSchema = z
  .object({
    targetFlowId: z
      .number({ message: 'Fluxo de destino é obrigatório' })
      .nullable(),
    targetFlowName: z.string().nullable().optional(),
    deletable: z.boolean().optional(),
  })
  .refine((data) => data.targetFlowId !== null, {
    message: 'Fluxo de destino é obrigatório',
    path: ['targetFlowId'],
  })

export type FlowConnectionNodeData = z.infer<
  typeof FlowConnectionNodeDataSchema
>

type FlowConnectionNodeProps = NodeProps<
  Node<FlowConnectionNodeData, typeof NODE_TYPE>
>

export function FlowConnectionNode({
  id,
  isConnectable,
  selected,
  data,
}: FlowConnectionNodeProps) {
  const meta = useMemo(() => getNodeDetail(NODE_TYPE), [])

  const [showNodePropertiesOf] = useFlowStore(
    useShallow((s) => [s.actions.sidebar.showNodePropertiesOf])
  )

  const [targetHandleId] = useState<string>(`${id}-target`)

  const deleteNode = useDeleteNode()

  const handleDeleteNode = () => {
    deleteNode(id)
  }

  const handleShowNodeProperties = useCallback(() => {
    showNodePropertiesOf({ id, type: NODE_TYPE })
  }, [id, showNodePropertiesOf])

  return (
    <NodeCard
      data-selected={selected}
      onDoubleClick={handleShowNodeProperties}
      nodeId={id}
    >
      <NodeCardHeader
        icon={meta.icon}
        title={meta.title}
        handleDeleteNode={handleDeleteNode}
        handleShowNodeProperties={handleShowNodeProperties}
        gradientColor={meta.gradientColor}
      />

      <NodeCardContent>
        <div className='flex flex-col p-4'>
          <div className='text-xs font-medium text-card-foreground'>
            Fluxo destino
          </div>
          <div className='mt-2 text-sm leading-snug'>
            {data.targetFlowId ? (
              <span className='rounded bg-card-foreground/10 px-1 py-0.5'>
                #{data.targetFlowId} {data.targetFlowName || ''}
              </span>
            ) : (
              <span className='italic text-card-foreground'>
                Nenhum fluxo selecionado...
              </span>
            )}
          </div>
        </div>

        <NodeCardDescription description='Transfere o usuário para outro fluxo específico' />

        <NodeCardFooter nodeId={id} />
      </NodeCardContent>

      <CustomHandle
        type='target'
        id={targetHandleId}
        position={Position.Left}
        isConnectable={isConnectable}
      />
    </NodeCard>
  )
}

export const metadata: RegisterNodeMetadata<FlowConnectionNodeData> = {
  type: NODE_TYPE,
  node: memo(FlowConnectionNode),
  detail: {
    icon: 'workflow',
    title: 'Conexão de Fluxo',
    description: 'Conecta e transfere para outro fluxo/robô',
    gradientColor: 'cyan',
  },
  connection: {
    inputs: 1,
    outputs: 0,
  },
  defaultData: {
    targetFlowId: null,
    targetFlowName: null,
  },
  propertyPanel: FlowConnectionPropertyPanel,
  validationSchema: FlowConnectionNodeDataSchema,
}
